import {ChangeDetectorRef, Component, InputSignal, OnDestroy, OnInit, OutputEmitterRef, effect, EffectRef, inject, input, output} from '@angular/core';
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {FormInputComponent} from "../form-input/form-input.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {FormLabelComponent} from "../form-label/form-label.component";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {TYPE} from "../../types/types";
import {forkJoin, of, Subscription} from "rxjs";
import {ApiService} from "../../services/api.service";
import {LoadingService} from "../../services/loading.service";
import {FormDropdownComponent} from "../form-dropdown/form-dropdown.component";
import {LanguageService} from "../../services/language.service";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {LinkComponent} from "../link/link.component";
import {FormCheckboxComponent} from "../form-checkbox/form-checkbox.component";
import {Dialog, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {FormButtonComponent} from "../form-button/form-button.component";
import {TooltipComponent} from "../tooltip/tooltip.component";
import {HeadlineComponent} from "../headline/headline.component";
import {ContentComponent} from "../content/content.component";
import {AvatarComponent} from "../avatar/avatar.component";
import {GroupMinimalResource} from '../../types/user/groupMinimalResource';
import {UserMinimalResource} from '../../types/knowledgebase-types/userMinimalResource';
import {CustomerActionType} from '../../types/user/customerActionType';

@Component({
    selector: 'app-form-user-group-mail',
    imports: [
        DropDownListModule,
        FormInputComponent,
        TranslatePipe,
        FormLabelComponent,
        ReactiveFormsModule,
        FormDropdownComponent,
        IconBadgeComponent,
        LinkComponent,
        FormCheckboxComponent,
        TooltipModule,
        FormButtonComponent,
        TooltipComponent,
        HeadlineComponent,
        ContentComponent,
        AvatarComponent
    ],
    templateUrl: './form-user-group-mail.component.html',
    styleUrl: './form-user-group-mail.component.scss'
})
export class FormUserGroupMailComponent implements OnInit, OnDestroy {
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private loadingService: LoadingService = inject(LoadingService);
  private languageService: LanguageService = inject(LanguageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);


  public user: InputSignal<number[]> = input<number[]>([]);
  public groups: InputSignal<number[]> = input<number[]>([]);
  public adminUserIDs: InputSignal<number[]> = input<number[]>([]);
  public type: InputSignal<'own' | 'all' | 'active' | 'inactive' | 'registration'> = input<'own' | 'all' | 'active' | 'inactive' | 'registration'>('own'); // TODO: too many types, should be simplified
  public method: InputSignal<'user' | 'group' | 'userGroup'> = input<'user' | 'group' | 'userGroup'>('userGroup');
  public allowEmails: InputSignal<boolean> = input(false);
  public allowPush: InputSignal<boolean> = input(false);
  public pushUserIDs: InputSignal<number[]> = input<number[]>([]);
  public pushGroupIDs: InputSignal<number[]> = input<number[]>([]);
  public mails: InputSignal<string[]> = input<string[]>([]);
  public allowAdmins: InputSignal<boolean> = input(false);
  public isLimitedByAllowedIDs: InputSignal<boolean> = input(false);
  public allowedUserIDs: InputSignal<number[]> = input<number[]>([]);
  public allowedGroupIDs: InputSignal<number[]> = input<number[]>([]);
  public readonly: InputSignal<boolean> = input(false);
  public showSelectedParticipants: InputSignal<boolean> = input(true);
  public allowGroupsToResolve: InputSignal<boolean> = input(false);
  public hasError: InputSignal<boolean> = input(false);
  public hasErrorField: InputSignal<boolean> = input(true);
  public singleSelection: InputSignal<boolean> = input(false);
  public singleSelectionStays: InputSignal<boolean> = input(false);
  public can: InputSignal<CustomerActionType | ''> = input.required<CustomerActionType | ''>();
  public userChange: OutputEmitterRef<number[]> = output<number[]>();
  public groupsChange: OutputEmitterRef<number[]> = output<number[]>();
  public mailsChange: OutputEmitterRef<string[]> = output<string[]>();
  public pushUserIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public pushGroupIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public adminUserIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public adminGroupIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public userNamesChange: OutputEmitterRef<string[]> = output<string[]>();
  public groupNamesChange: OutputEmitterRef<string[]> = output<string[]>();
  public userModelChange: OutputEmitterRef<TYPE.UserListItem[]> = output<TYPE.UserListItem[]>();
  public groupModelChange: OutputEmitterRef<TYPE.GroupListItem[]> = output<TYPE.GroupListItem[]>();
  public defaultPushState: InputSignal<boolean> = input(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupOptions: any[] = [];
  public userGroupMailForm: FormGroup = this.formBuilder.group({
    userGroup: [''],
    addMail: ['', [Validators.email]],
    userGroupMailFilter: ['all']
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupMailFilter: any[] = [];
  public participants: (TYPE.FieldSettingsModelWithId)[] = [];
  private allAllowedOptions: TYPE.FieldSettingsModelWithId[] = [];
  private subscriptions: Subscription[] = [];
  private page: number = 0;
  private searchString: string = '';
  private currentLoadingType: 'user' | 'group' = 'group';
  private dialogInstance: Dialog | null = null;
  private loadForbidden: boolean = false;
  private allowedOptionsLoaded: boolean = false;
  private initialsLoadVersion: number = 0;

  public constructor() {
    effect(() => {
      if (!this.allowPush()) {
        return;
      }

      let changed = false;
      this.participants.forEach((participant) => {
        if (participant.type === 'mail' || participant.push !== null && participant.push !== undefined) {
          return;
        }

        participant.push = false;
        changed = true;
      });

      if (changed) {
        this.emitPushChanges();
        this.cdr.markForCheck();
      }
    });
  }

  private inputsChangeEffect: EffectRef = effect(() => {
    this.user();
    this.groups();
    this.mails();
    this.adminUserIDs();
    this.pushUserIDs();
    this.pushGroupIDs();

    if (!this.hasDifferentParticipantsFromInputs()) {
      return;
    }

    this.loadInitials();
  });

  public ngOnInit(): void {
    if (this.method() === 'user') {
      this.currentLoadingType = 'user';
    }

    if (this.allowEmails()) {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'mail', name: this.languageService.getTranslationByKey('SHOWMAILONLY')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    } else {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    }

    this.load();
    this.loadInitials();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private load(): void {
    if (this.loadForbidden) {
      return;
    }

    if (this.isLimitedByAllowedIDs()) {
      this.loadAllowedOptions();
      return;
    }

    if (this.singleSelectionStays()) {
      this.ensureSingleSelectionPlaceholder();
    }

    if (this.currentLoadingType === 'group') {
      this.loadGroups();
    } else {
      if (this.method() === 'user' || this.method() === 'userGroup') {
        this.loadUser();
      }
    }
  }

  private loadGroups(): void {
    this.subscriptions.push(this.apiService.getGroups(this.page, this.searchString, true, this.type(), this.can()).subscribe((groupListItems: GroupMinimalResource[]) => {

      const allowedGroupIDsSet = new Set(this.allowedGroupIDs());
      const groupListItemsTmp = (this.isLimitedByAllowedIDs() ? groupListItems.filter(group => allowedGroupIDsSet.has(group.id)) : groupListItems).map(group => ({
        id: group.id,
        description: group.description,
        count: group.user_count,
        image: group.avatar?.path ?? '',
        name: group.name,
        type: 'group'
      }));

      // Create new array reference for change detection
      this.userGroupOptions = [...this.userGroupOptions, ...groupListItemsTmp];

      if (!groupListItemsTmp.length) {
        this.currentLoadingType = 'user';
        this.page = 0;
        this.load();
      }

      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private loadUser(): void {
    this.subscriptions.push(this.apiService.getUsers(this.page, this.searchString, this.type(), this.can()).subscribe((userListItems: UserMinimalResource[]) => {

      const allowedUserIDsSet = new Set(this.allowedUserIDs());
      const userListItemsTmp = (this.isLimitedByAllowedIDs() ? userListItems.filter(user => allowedUserIDsSet.has(user.id)) : userListItems).map(user => ({
        id: user.id,
        image: user.avatar?.path ?? '',
        name: user.last_name + ', ' + user.first_name,
        type: 'user'
      }));

      // Create new array reference for change detection
      this.userGroupOptions = [...this.userGroupOptions, ...userListItemsTmp];

      if (!userListItemsTmp.length) {
        this.loadForbidden = true;
      }

      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private loadAllowedOptions(): void {
    if (this.allowedOptionsLoaded) {
      this.userGroupOptions = [...this.allAllowedOptions];
      this.ensureSingleSelectionPlaceholder();
      this.removeExistingParticipants();
      return;
    }

    const groups$ = this.method() !== 'user' && this.allowedGroupIDs().length
      ? this.apiService.getGroupsByIds(this.allowedGroupIDs())
      : of([]);
    const users$ = this.method() !== 'group' && this.allowedUserIDs().length
      ? this.apiService.getUsersByIds(this.allowedUserIDs())
      : of([]);

    this.subscriptions.push(forkJoin({groups: groups$, users: users$}).subscribe(({groups, users}) => {
      const allowedGroupIDsSet = new Set(this.allowedGroupIDs());
      const allowedUserIDsSet = new Set(this.allowedUserIDs());
      const groupOptions = (groups as GroupMinimalResource[])
        .filter((group) => allowedGroupIDsSet.has(group.id))
        .map((group) => ({
          id: group.id,
          description: group.description,
          count: group.user_count,
          image: group.avatar?.path ?? '',
          name: group.name,
          type: 'group'
        }));

      const userOptions = (users as UserMinimalResource[])
        .filter((user) => allowedUserIDsSet.has(user.id))
        .map((user) => ({
          id: user.id,
          image: user.avatar?.path ?? '',
          name: user.last_name + ', ' + user.first_name,
          type: 'user'
        }));

      this.allAllowedOptions = [...groupOptions, ...userOptions];
      this.userGroupOptions = [...this.allAllowedOptions];
      this.allowedOptionsLoaded = true;
      this.loadForbidden = true;
      this.ensureSingleSelectionPlaceholder();
      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private ensureSingleSelectionPlaceholder(): void {
    if (!this.singleSelectionStays()) {
      return;
    }

    const hasPlaceholder = this.userGroupOptions.some((item) => item.id === 0 && item.name === '' && !item.type);
    if (!hasPlaceholder) {
      // Create new array reference instead of mutating
      this.userGroupOptions = [{
        id: 0,
        description: '',
        count: 0,
        image: '',
        name: ''
      }, ...this.userGroupOptions];
    }
  }

  private removeExistingParticipants(): void {
    // Create new array reference for change detection
    this.userGroupOptions = [...this.userGroupOptions.filter((item) => {
      return !this.participants.find((participant) => participant.id === item.id && participant.type === item.type);
    })];
  }

  public loadNextPage(): void {
    if (!this.loadingService.getLoadingIndicator()) {
      if (this.isLimitedByAllowedIDs()) {
        return;
      }
      this.page++;
      this.load();
    }
  }

  public search(searchString: string): void {
    if (!this.loadingService.getLoadingIndicator()) {
      if (this.isLimitedByAllowedIDs()) {
        this.searchString = searchString;
        // Create new array reference for change detection
        this.userGroupOptions = [...this.filterAllowedOptions(searchString)];
        this.ensureSingleSelectionPlaceholder();
        this.removeExistingParticipants();
        // Trigger change detection for OnPush parent components
        this.cdr.markForCheck();
        return;
      }
      if (this.method() !== 'user') {
        this.currentLoadingType = 'group';
      }
      this.searchString = searchString;
      this.page = 0;
      // Create new array reference for change detection
      this.userGroupOptions = [];
      this.loadForbidden = false;
      this.load();
    }
  }

  private filterAllowedOptions(searchString: string): TYPE.FieldSettingsModelWithId[] {
    const normalized = searchString.trim().toLowerCase();
    if (!normalized) {
      return [...this.allAllowedOptions];
    }

    return this.allAllowedOptions.filter((item) => item.name?.toLowerCase().includes(normalized));
  }

  private refreshAvailableOptions(): void {
    // Create new array reference for change detection
    this.userGroupOptions = [...this.filterAllowedOptions(this.searchString)];
    this.ensureSingleSelectionPlaceholder();
    this.removeExistingParticipants();
    // Trigger change detection for OnPush parent components
    this.cdr.markForCheck();
  }

  public addUserGroup(userOrGroup: string | number | TYPE.FieldSettingsModelWithId): void {
    if (typeof userOrGroup === 'string' || typeof userOrGroup === 'number') {
      userOrGroup = this.userGroupOptions.find((item) => item.id === userOrGroup) as TYPE.FieldSettingsModelWithId;
    }
    if (this.participants.find((item) => item.id === userOrGroup.id && item.type === userOrGroup.type)) {
      // already added
    } else {
      userOrGroup.push = this.getNewPushState();
      this.participants.push(userOrGroup);
      this.userGroupMailForm.addControl(userOrGroup.type! + userOrGroup.id, this.formBuilder.control(false));
    }
    setTimeout(() => {
      if (!this.singleSelectionStays()) {
        this.userGroupMailForm.controls['userGroup'].setValue(null);
        this.removeExistingParticipants();
      }
      this.updateEmitters();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }, 500);
  }

  public addMail(): void {
    const mail = this.userGroupMailForm.controls['addMail'].value;

    if (mail && this.userGroupMailForm.controls['addMail'].valid && !this.participants.find((item) => item.id === mail && item.type === 'mail')) {
      this.participants.push({
        id: mail,
        name: mail,
        type: 'mail',
        push: this.getNewPushState()
      });

      this.userGroupMailForm.addControl('mail' + mail, this.formBuilder.control(false));
      this.userGroupMailForm.controls['addMail'].setValue('');
      this.updateEmitters();
    }
  }

  public selectAll(): void {
    this.participants.forEach((item) => {
      this.userGroupMailForm.controls[item.type! + item.id].setValue(true);
    });
  }

  public deselectAll(): void {
        this.participants.forEach((item) => {
      this.userGroupMailForm.controls[item.type! + item.id].setValue(false);
    });
  }

  public allSelected(): boolean {
    return this.participants.every((item) => {
      return this.userGroupMailForm.controls[item.type! + item.id].value;
    })
  }

  public removeSelected(): void {
    this.participants.forEach((item) => {
      if (this.userGroupMailForm.controls[item.type! + item.id].value) {
        this.userGroupMailForm.removeControl(item.type! + item.id);
      }
    });
    this.participants = this.participants.filter((item) => this.userGroupMailForm.controls[item.type! + item.id]);
    if (this.isLimitedByAllowedIDs()) {
      this.refreshAvailableOptions();
    }
    this.updateEmitters();
  }

  public showDeleteOverlay(item: TYPE.FieldSettingsModelWithId): void {
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.languageService.getTranslationByKey('DELETE'),
        click: () => this.showDeleteOverlayConfirmed(item),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private showDeleteOverlayConfirmed(item: TYPE.FieldSettingsModelWithId): void {
    this.userGroupMailForm.removeControl(item.type! + item.id);
    this.participants = this.participants.filter((participant) => participant.id !== item.id || participant.type !== item.type);
    this.dialogInstance?.hide();
    
    if (this.isLimitedByAllowedIDs()) {
      this.refreshAvailableOptions();
    } else {
      if (item.type !== 'mail' && !this.singleSelectionStays()) {
        this.addParticipantBackToOptions(item);
      }
    }
    
    this.userGroupMailForm.controls['userGroup'].setValue(null);
    this.updateEmitters();
    // Trigger change detection for OnPush parent components
    this.cdr.markForCheck();
  }

  private sortUserGroupOptions(
    items: TYPE.FieldSettingsModelWithId[]
  ): TYPE.FieldSettingsModelWithId[] {
    return [...items].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'group' ? -1 : 1;
      }

      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }

  private addParticipantBackToOptions(item: TYPE.FieldSettingsModelWithId): void {
    if (item.type === 'mail' || this.singleSelectionStays()) {
      return;
    }

    if (this.userGroupOptions.some((option) => option.id === item.id && option.type === item.type)) {
      return;
    }

    this.userGroupOptions = this.sortUserGroupOptions([
      ...this.userGroupOptions,
      item
    ]);
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  private loadInitials(): void {
    const loadVersion = ++this.initialsLoadVersion;
    const users$ = this.user().length
      ? this.apiService.getUsersByIds(this.user())
      : of([]);

    const groups$ = this.groups().length
      ? this.apiService.getGroupsByIds(this.groups())
      : of([]);

    this.subscriptions.push(forkJoin({users: users$, groups: groups$}).subscribe(({users, groups}) => {
      if (loadVersion !== this.initialsLoadVersion) {
        return;
      }

      const requestedUserIDs = new Set(this.user());
      const requestedGroupIDs = new Set(this.groups());
      const nextParticipants: TYPE.FieldSettingsModelWithId[] = [];

      (users as UserMinimalResource[])
        .filter((user) => requestedUserIDs.has(user.id))
        .forEach((user) => {
          nextParticipants.push({
            id: user.id,
            image: user.avatar?.path ?? '',
            name: user.last_name + ', ' + user.first_name,
            type: 'user',
            push: this.getLoadedPushState({id: user.id, type: 'user'}),
            admin: this.adminUserIDs().includes(user.id)
          });
        });

      (groups as GroupMinimalResource[])
        .filter((group) => requestedGroupIDs.has(group.id))
        .forEach((group) => {
          nextParticipants.push({
            id: group.id,
            description: group.description,
            count: group.user_count,
            image: group.avatar?.path ?? '',
            name: group.name,
            type: 'group',
            push: this.getLoadedPushState({id: group.id, type: 'group'})
          });
        });
      //though push: push: this.getLoadedPushState({id: mail, type: 'mail'}) is not implemented on the backend side for consistency I will still set it here for future use
      this.mails().forEach((mail) => {
        nextParticipants.push({
          id: mail,
          name: mail,
          type: 'mail',
          push: this.getLoadedPushState({id: mail, type: 'mail'})
        });
      });

      this.syncParticipantControls(nextParticipants);
      this.participants = nextParticipants;
      this.removeExistingParticipants();
      this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
      this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
      this.cdr.markForCheck();
    }));
  }

  private hasDifferentParticipantsFromInputs(): boolean {
    const participantUsers = this.participants
      .filter((item) => item.type === 'user')
      .map((item) => item.id as number);
    const participantGroups = this.participants
      .filter((item) => item.type === 'group')
      .map((item) => item.id as number);
    const participantMails = this.participants
      .filter((item) => item.type === 'mail')
      .map((item) => item.id as string);
    const participantAdminUsers = this.participants
      .filter((item) => item.type === 'user' && item.admin)
      .map((item) => item.id as number);
    const participantPushUsers = this.participants
      .filter((item) => item.type === 'user' && item.push)
      .map((item) => item.id as number);
    const participantPushGroups = this.participants
      .filter((item) => item.type === 'group' && item.push)
      .map((item) => item.id as number);

    return this.getComparableKey(participantUsers) !== this.getComparableKey(this.user())
      || this.getComparableKey(participantGroups) !== this.getComparableKey(this.groups())
      || this.getComparableKey(participantMails) !== this.getComparableKey(this.mails())
      || this.getComparableKey(participantAdminUsers) !== this.getComparableKey(this.adminUserIDs())
      || this.getComparableKey(participantPushUsers) !== this.getComparableKey(this.pushUserIDs())
      || this.getComparableKey(participantPushGroups) !== this.getComparableKey(this.pushGroupIDs());
  }

  private syncParticipantControls(participants: TYPE.FieldSettingsModelWithId[]): void {
    Object.keys(this.userGroupMailForm.controls)
      .filter((controlName) => controlName !== 'userGroup' && controlName !== 'addMail' && controlName !== 'userGroupMailFilter')
      .forEach((controlName) => this.userGroupMailForm.removeControl(controlName));

    participants.forEach((participant) => {
      this.userGroupMailForm.addControl(participant.type! + participant.id, this.formBuilder.control(false));
    });
  }

  private getComparableKey(values: (string | number)[]): string {
    return [...values]
      .map((value) => String(value))
      .sort()
      .join('|');
  }

  private updateEmitters(): void {
    this.userChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.id as number));
    this.groupsChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.id as number));
    this.mailsChange.emit(this.participants.filter((item) => item.type === 'mail').map((item) => item.id as string));
    this.emitPushChanges();
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.userNamesChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.name as string));
    this.groupNamesChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.name as string));
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public togglePush(participant: TYPE.FieldSettingsModelWithId): void {
    participant.push = !participant.push;
    this.emitPushChanges();
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public hasPushParticipants(): boolean {
    return this.participants.some((item) =>
      item.type === 'user' || item.type === 'group' || item.type === 'mail'
    );
  }

  public allPushEnabled(): boolean {
    const pushParticipants = this.participants.filter((item) => item.type === 'user' || item.type === 'group');
    return pushParticipants.length > 0 && pushParticipants.every((item) => item.push);
  }

  public enablePushForAll(): void {
    this.setPushForAll(true);
  }

  public disablePushForAll(): void {
    this.setPushForAll(false);
  }

  private getLoadedPushState(
    participant: Pick<TYPE.FieldSettingsModelWithId, 'id' | 'type'>
  ): boolean | null {
    if (!this.allowPush()) {
      return null;
    }

    if (participant.type === 'user') {
      return this.pushUserIDs().includes(participant.id as number);
    }

    if (participant.type === 'group') {
      return this.pushGroupIDs().includes(participant.id as number);
    }

    if (participant.type === 'mail') {
      return null;
    }

    return null;
  }

  private getNewPushState(): boolean | null {
    if (!this.allowPush()) {
      return null;
    }

    return this.defaultPushState();
  }

  private emitPushChanges(): void {
    this.pushUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.push).map((item) => item.id as number));
    this.pushGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.push).map((item) => item.id as number));
  }

  private setPushForAll(value: boolean): void {
    this.participants.forEach((participant) => {
      if (participant.type === 'user' || participant.type === 'group' || participant.type === 'mail') {
        participant.push = value;
      }
    });

    this.emitPushChanges();
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
    this.cdr.markForCheck();
  }

  public toggleAdmin(participant: TYPE.FieldSettingsModelWithId): void {
    participant.admin = !participant.admin;
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public mapFieldSettingsToUserListItem(field: TYPE.FieldSettingsModelWithId): TYPE.UserListItem {
    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      avatar: field.image ?? '', // falls image null ist, leeres Bild
      first_name: field.name?.split(' ')[1].replace(',', '') ?? '', // Annahme: Name enthält Vor- und Nachname
      last_name: field.name?.split(' ')[0].replace(',', '') ?? '', // fallback falls name nicht gesetzt ist
      admin: field.admin ?? false
    };
  }
  public mapFieldSettingsToGroupListItem(field: TYPE.FieldSettingsModelWithId): TYPE.GroupListItem {
    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      name: field.name ?? '',
      description: field.description ?? '',
      image: field.image ?? '',
      count: field.count ?? 0
    };
  }

  public resolveGroupToUsers(participant: TYPE.FieldSettingsModelWithId): void {
    if (participant.type !== 'group' || typeof participant.id !== 'number') {
      return;
    }

    const participantType = participant.type;

    this.subscriptions.push(this.apiService.getUsersByGroupId(participant.id).subscribe((users: UserMinimalResource[]) => {
      const existingUsersById = new Map<number, TYPE.FieldSettingsModelWithId>(
        this.participants
          .filter((item) => item.type === 'user' && typeof item.id === 'number')
          .map((item) => [item.id as number, item])
      );

      const resolvedUsers = users.reduce((accumulator: TYPE.FieldSettingsModelWithId[], user) => {
        if (existingUsersById.has(user.id) || accumulator.some((entry) => entry.id === user.id && entry.type === 'user')) {
          return accumulator;
        }

        accumulator.push({
          id: user.id,
          image: user.avatar?.path ?? '',
          name: user.last_name + ', ' + user.first_name,
          type: 'user',
          push: participant.push ?? this.getNewPushState(),
          admin: false
        });

        return accumulator;
      }, []);

      this.userGroupMailForm.removeControl(participantType + participant.id);
      resolvedUsers.forEach((user) => {
        this.userGroupMailForm.addControl(user.type! + user.id, this.formBuilder.control(false));
      });

      this.participants = this.participants
        .filter((item) => item.id !== participant.id || item.type !== participantType)
        .concat(resolvedUsers);

      if (this.isLimitedByAllowedIDs()) {
        this.refreshAvailableOptions();
      } else {
        this.addParticipantBackToOptions(participant);
      }

      this.userGroupMailForm.controls['userGroup'].setValue(null);
      this.updateEmitters();
      this.cdr.markForCheck();
    }));
  }
}
